import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Stage, Layer, Rect, Circle, Transformer, Text } from 'react-konva';
import Konva from 'konva';
import { Site, Location, Equipment, DesignObject } from '../types/interfaces';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import {
  createDesignObject,
  normalizeDesignObjects,
  updateObjectById,
  removeObjectById,
  moveObjectToEnd,
  designNeedsAssignment,
  designNeedsLabels,
  isKonvaSafeId,
  sizeFromTransform,
  MIN_SHAPE_SIZE,
  alignObjects,
  distributeObjects,
  moveObjects,
  idsInBox,
  nextSelection,
  selectionOnPointerDown,
  selectionOnClick,
  tapStart,
  tapMove,
  isDeliberateTap,
  TapGesture,
  PendingSelect,
  AlignMode,
  DistributeAxis,
  ShapeBounds,
} from '../utils/facilityDesignerUtils';
import {
  createModifierTracker,
  isModifierHeld,
  HeldModifiers,
  ModifierFlags,
  LONG_PRESS_MS,
} from '../utils/heldModifiers';
import { lockMenuSwipe, unlockMenuSwipe } from '../utils/edgeSwipeMenu';
import {
  canonicalDesign,
  isDesignDirty,
  saveDraft,
  loadDraft,
  clearDraft,
  isLeavingClick,
  DesignDraft,
} from '../utils/designDraft';
import { resolveFacilityLocation, isTaproomLocation } from '../utils/facilityLocationResolve';

const FACILITY_DESIGNER_DRAG_LOCK = 'facility-designer-drag';

const PHONE_QUERY = '(max-width: 767px)';
function readIsPhone(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    try {
      return window.matchMedia(PHONE_QUERY).matches;
    } catch {
      /* fall through */
    }
  }
  return (window.innerWidth || 1024) <= 767;
}

/** Phone layout (same 767px breakpoint as the mobile bottom nav). */
export function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState(readIsPhone);
  useEffect(() => {
    const on = () => setIsPhone(readIsPhone());
    window.addEventListener('resize', on);
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(PHONE_QUERY) : null;
    mq?.addEventListener?.('change', on);
    return () => {
      window.removeEventListener('resize', on);
      mq?.removeEventListener?.('change', on);
    };
  }, []);
  return isPhone;
}

type TouchLike = { clientX: number; clientY: number };
/** First touch point (touchend reports it in changedTouches). */
function touchPoint(evt: unknown): { x: number; y: number } | null {
  const e = evt as { touches?: ArrayLike<TouchLike>; changedTouches?: ArrayLike<TouchLike> } | null;
  const t = (e?.touches && e.touches[0]) || (e?.changedTouches && e.changedTouches[0]);
  if (!t) return null;
  return { x: Number(t.clientX) || 0, y: Number(t.clientY) || 0 };
}

/** Total scroll of the page + scrolling ancestors (.content / .page-container scroll, not window). */
function scrollOffset(el: HTMLElement | null): number {
  let total = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
  for (let n: HTMLElement | null = el; n; n = n.parentElement) total += n.scrollTop || 0;
  return total;
}

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

const FacilityDesigner: React.FC = () => {
  const [siteId, setSiteId] = useState<string>('DSP-AL-20010');
  const [sites, setSites] = useState<Site[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [objects, setObjects] = useState<DesignObject[]>([]);
  // Multi-select: shift/ctrl/cmd-click, drag-select box, or the "Select multiple" toggle (touch).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedObjectId = selectedIds.length === 1 ? selectedIds[0] : null;
  const setSelectedObjectId = useCallback((id: string | null) => setSelectedIds(id ? [id] : []), []);
  const [multiMode, setMultiMode] = useState(false);
  const [selectBox, setSelectBox] = useState<ShapeBounds | null>(null);
  const groupDragRef = useRef<{ id: string; start: Record<string, { x: number; y: number }> } | null>(null);
  useEffect(() => () => unlockMenuSwipe(FACILITY_DESIGNER_DRAG_LOCK), []);
  const historyRef = useRef<DesignObject[][]>([]);
  const [historyLen, setHistoryLen] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const transformerRef = useRef<Konva.Transformer | null>(null);
  const stageRef = useRef<Konva.Stage | null>(null);
  const shapeRefs = useRef<Record<string, Konva.Node>>({});
  const canvasBoxRef = useRef<HTMLDivElement | null>(null);
  // Empty-canvas touch: only a deliberate tap deselects (a scroll/drag never does).
  const emptyTapRef = useRef<TapGesture | null>(null);
  const lastTouchAtRef = useRef(0);
  const isPhone = useIsPhone();

  // ---- Unsaved-changes guard ----
  const [baseline, setBaseline] = useState<string | null>(null);
  const dirty = isDesignDirty(objects, baseline);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const [draftOffer, setDraftOffer] = useState<DesignDraft | null>(null);
  const [guardPrompt, setGuardPrompt] = useState<{ resume: () => void; label: string } | null>(null);
  const bypassGuardRef = useRef(false);

  useEffect(() => {
    apiFetch(`${API_BASE_URL}/api/sites`)
      .then((res) => res.json())
      .then((data) => setSites(Array.isArray(data) ? data : []))
      .catch((err) => setError('Failed to load sites: ' + err.message));
  }, []);

  useEffect(() => {
    if (!siteId) {
      setLocations([]);
      setEquipment([]);
      setObjects([]);
      setSelectedObjectId(null);
      return;
    }
    Promise.all([
      apiFetch(`${API_BASE_URL}/api/locations?siteId=${siteId}&includeTaproom=1`).then((res) => res.json()),
      apiFetch(`${API_BASE_URL}/api/equipment?siteId=${siteId}`).then((res) => res.json()),
      apiFetch(`${API_BASE_URL}/api/facility-design?siteId=${siteId}`).then((res) => res.json()),
    ])
      .then(([locationsData, equipmentData, designData]) => {
        setLocations(Array.isArray(locationsData) ? locationsData : []);
        setEquipment(Array.isArray(equipmentData) ? equipmentData : []);
        const loaded = Array.isArray(designData?.objects) ? designData.objects : [];
        const normalized = normalizeDesignObjects(loaded);
        setObjects(normalized);
        setBaseline(canonicalDesign(normalized));
        historyRef.current = [];
        setHistoryLen(0);
        const draft = loadDraft(siteId);
        setDraftOffer(draft && canonicalDesign(draft.objects) !== canonicalDesign(normalized) ? draft : null);
        setSelectedObjectId(null);
        setError(designData?.error || null);
      })
      .catch((err) => setError('Failed to load data: ' + err.message));
  }, [siteId]);

  /** Keep Transformer attached to the selected node across re-renders / z-order moves. */
  useEffect(() => {
    const tr = transformerRef.current as Konva.Transformer | null;
    // Resize handles only for a single shape; a multi-selection is shown by outline.
    if (!tr || typeof tr.nodes !== 'function') return;
    if (!selectedObjectId) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const node =
      shapeRefs.current[selectedObjectId] ||
      (typeof stageRef.current?.findOne === 'function'
        ? stageRef.current.findOne(`#${selectedObjectId}`)
        : null);
    if (node && typeof (node as Konva.Node).moveToTop === 'function') {
      // Raise selected shape above siblings so it receives pointer events when overlapping.
      (node as Konva.Node).moveToTop();
      // Keep the shape's label above it, or the label vanishes under the fill.
      const label = stageRef.current?.findOne?.(`.label-${selectedObjectId}`);
      label?.moveToTop();
      tr.nodes([node as Konva.Node]);
      tr.moveToTop();
      tr.getLayer()?.batchDraw();
    } else if (node) {
      // Test / non-Konva refs: selection still tracked in React state.
      tr.nodes([]);
    } else {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
    }
  }, [selectedObjectId, objects]);

  const addObject = (type: 'Tank' | 'Barrel' | 'Storage') => {
    if (!siteId) {
      setError('Please select a site first');
      return;
    }
    setObjects((prev) => {
      const created = createDesignObject(type, prev.length);
      setSelectedObjectId(created.id);
      return [...prev, created];
    });
    setError(null);
  };

  /** Snapshot for Undo (align, distribute, group move, delete). */
  const pushHistory = (snapshot: DesignObject[]) => {
    historyRef.current = [...historyRef.current.slice(-29), snapshot];
    setHistoryLen(historyRef.current.length);
  };

  const undo = useCallback(() => {
    const prev = historyRef.current.pop();
    setHistoryLen(historyRef.current.length);
    if (prev) setObjects(prev);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  const deleteObject = () => {
    if (!selectedIds.length) return;
    pushHistory(objects);
    setObjects((prev) => selectedIds.reduce((acc, id) => removeObjectById(acc, id), prev));
    setSelectedIds([]);
  };

  const applyAlign = (mode: AlignMode) => {
    if (selectedIds.length < 2) return;
    pushHistory(objects);
    setObjects((prev) => alignObjects(prev, selectedIds, mode));
  };

  const applyDistribute = (axis: DistributeAxis) => {
    if (selectedIds.length < 3) return;
    pushHistory(objects);
    setObjects((prev) => distributeObjects(prev, selectedIds, axis));
  };

  const handleDragStart = (id: string) => {
    pendingSelectRef.current = null; // a drag is not a click
    cancelLongPress(); // moving the finger means drag, not long-press
    lockMenuSwipe(FACILITY_DESIGNER_DRAG_LOCK);
    const selectedIds = selectedIdsRef.current;
    if (selectedIds.length < 2 || !selectedIds.includes(id)) {
      groupDragRef.current = null;
      return;
    }
    const start: Record<string, { x: number; y: number }> = {};
    for (const o of objects) if (selectedIds.includes(o.id)) start[o.id] = { x: o.x, y: o.y };
    groupDragRef.current = { id, start };
  };

  const handleDragMove = (id: string, x: number, y: number) => {
    const g = groupDragRef.current;
    if (!g || g.id !== id) return;
    const dx = x - g.start[id].x;
    const dy = y - g.start[id].y;
    for (const otherId of Object.keys(g.start)) {
      if (otherId === id) continue;
      const node = shapeRefs.current[otherId] as Konva.Node | null | undefined;
      if (node && typeof node.position === 'function') {
        node.position({ x: g.start[otherId].x + dx, y: g.start[otherId].y + dy });
      }
    }
    const layer = (shapeRefs.current[id] as Konva.Node | undefined)?.getLayer?.();
    layer?.batchDraw();
  };

  const saveDesign = async (): Promise<boolean> => {
    if (!siteId) {
      setError('Please select a site to save the design');
      return false;
    }
    if (designNeedsAssignment(objects)) {
      setError('All objects must have an assigned Location or Equipment');
      return false;
    }
    if (designNeedsLabels(objects)) {
      setError('All objects must have a name and abbreviation');
      return false;
    }
    // Persist Konva-safe ids so reload selection keeps working.
    const payloadObjects = normalizeDesignObjects(objects);
    const payload = { siteId, objects: payloadObjects };
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/facility-design`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save design: ${errorText}`);
      }
      const data = await response.json();
      const savedObjects = normalizeDesignObjects(data.objects || payloadObjects);
      setObjects(savedObjects);
      setBaseline(canonicalDesign(savedObjects));
      clearDraft(siteId);
      setDraftOffer(null);
      setSuccessMessage(data.message || 'Design saved successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
      setError(null);
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError('Failed to save design: ' + message);
      return false;
    }
  };

  // Local draft per site while there are unsaved changes (restore offered on reload).
  useEffect(() => {
    if (!siteId || baseline == null) return;
    if (dirty) saveDraft(siteId, objects);
  }, [objects, dirty, siteId, baseline]);

  // Browser refresh / close
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // In-app navigation: menu items, navbar, links (capture phase, before React Router).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!dirtyRef.current || bypassGuardRef.current) return;
      const el = isLeavingClick(e.target as Element | null);
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      setGuardPrompt({
        label: (el.textContent || '').trim().slice(0, 40) || 'another page',
        resume: () => {
          bypassGuardRef.current = true;
          try {
            (el as HTMLElement).click();
          } finally {
            bypassGuardRef.current = false;
          }
        },
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  const guardThen = (label: string, action: () => void) => {
    if (!dirtyRef.current) return action();
    setGuardPrompt({ label, resume: action });
  };

  const resolveGuard = async (choice: 'save' | 'discard' | 'cancel') => {
    const prompt = guardPrompt;
    if (!prompt) return;
    if (choice === 'cancel') return setGuardPrompt(null);
    if (choice === 'save') {
      const ok = await saveDesign();
      if (!ok) return setGuardPrompt(null); // keep the user here to fix the error
    } else {
      if (siteId) clearDraft(siteId);
      dirtyRef.current = false;
      setBaseline(canonicalDesign(objects));
    }
    setGuardPrompt(null);
    prompt.resume();
  };

  const handleDragEnd = (id: string, x: number, y: number) => {
    unlockMenuSwipe(FACILITY_DESIGNER_DRAG_LOCK);
    const g = groupDragRef.current;
    groupDragRef.current = null;
    if (g && g.id === id) {
      const dx = x - g.start[id].x;
      const dy = y - g.start[id].y;
      const ids = Object.keys(g.start);
      pushHistory(objects);
      setObjects((prev) => moveObjects(prev, ids, dx, dy));
      return;
    }
    setObjects((prev) => updateObjectById(prev, id, { x, y }));
  };

  const handleTransformEnd = (id: string, node: Konva.Node) => {
    // Root cause of "resize snaps back": the size used to be computed inside the
    // setObjects updater, which React runs later, after scale was reset to 1, so
    // radius/width stayed the same. Compute it now, then reset scale.
    const isCircle = node.getClassName() === 'Circle';
    const patch = isCircle
      ? sizeFromTransform('circle', {
          x: node.x(),
          y: node.y(),
          radius: (node as Konva.Circle).radius(),
          scaleX: node.scaleX(),
          scaleY: node.scaleY(),
        })
      : sizeFromTransform('rectangle', {
          x: node.x(),
          y: node.y(),
          width: (node as Konva.Rect).width(),
          height: (node as Konva.Rect).height(),
          scaleX: node.scaleX(),
          scaleY: node.scaleY(),
        });
    node.scaleX(1);
    node.scaleY(1);
    if (isCircle && patch.radius != null) (node as Konva.Circle).radius(patch.radius);
    if (!isCircle) {
      if (patch.width != null) (node as Konva.Rect).width(patch.width);
      if (patch.height != null) (node as Konva.Rect).height(patch.height);
    }
    setObjects((prev) => updateObjectById(prev, id, patch));
  };

  const assignLocation = (locationId: number) => {
    if (!selectedObjectId) return;
    const selectedLocation = locations.find((loc) => loc.locationId === locationId);
    if (!selectedLocation) {
      setError('Selected location not found');
      return;
    }
    setObjects((prev) =>
      updateObjectById(prev, selectedObjectId, {
        locationId,
        equipmentId: undefined,
        name: selectedLocation.name,
        abbreviation:
          selectedLocation.abbreviation || selectedLocation.name.slice(0, 3).toUpperCase(),
      })
    );
  };

  const assignEquipment = (equipmentId: number) => {
    if (!selectedObjectId) return;
    const selectedEquipment = equipment.find((eq) => eq.equipmentId === equipmentId);
    if (!selectedEquipment) {
      setError('Selected equipment not found');
      return;
    }
    setObjects((prev) =>
      updateObjectById(prev, selectedObjectId, {
        equipmentId,
        locationId: undefined,
        name: selectedEquipment.name,
        abbreviation:
          selectedEquipment.abbreviation || selectedEquipment.name.slice(0, 3).toUpperCase(),
      })
    );
  };

  const selectedIdsRef = useRef<string[]>([]);
  selectedIdsRef.current = selectedIds;
  const pendingSelectRef = useRef<PendingSelect | null>(null);
  type Mods = ModifierFlags;
  const heldRef = useRef<HeldModifiers | null>(null);
  useEffect(() => {
    const tracker = createModifierTracker(window);
    heldRef.current = tracker.held;
    return () => tracker.dispose();
  }, []);
  const isAdditive = (evt: Mods) => multiMode || isModifierHeld(evt, heldRef.current);

  // Long-press (touch fallback): hold a shape ~500ms to add/remove it.
  const longPressRef = useRef<{ timer: ReturnType<typeof setTimeout>; id: string; before: string[] } | null>(null);
  const longPressFiredRef = useRef<string | null>(null);
  const [longPressFlash, setLongPressFlash] = useState<string | null>(null);
  const cancelLongPress = () => {
    if (longPressRef.current) clearTimeout(longPressRef.current.timer);
    longPressRef.current = null;
  };
  useEffect(() => () => cancelLongPress(), []);

  /** Canvas shapes: select on pointer down (survives tiny drags), finish on click. */
  const handleShapePointerDown = (id: string, evt: Mods) => {
    const additive = isAdditive(evt);
    longPressFiredRef.current = null;
    cancelLongPress();
    const before = selectedIdsRef.current;
    longPressRef.current = {
      id,
      before,
      timer: setTimeout(() => {
        longPressRef.current = null;
        longPressFiredRef.current = id;
        pendingSelectRef.current = null;
        const next = nextSelection(before, id, true);
        selectedIdsRef.current = next;
        setSelectedIds(next);
        setLongPressFlash(id);
        setTimeout(() => setLongPressFlash(null), 600);
        try {
          (navigator as Navigator & { vibrate?: (ms: number) => boolean }).vibrate?.(30);
        } catch {
          /* no haptics */
        }
      }, LONG_PRESS_MS),
    };
    const { next, pending } = selectionOnPointerDown(selectedIdsRef.current, id, additive);
    pendingSelectRef.current = pending;
    if (next !== selectedIdsRef.current) {
      selectedIdsRef.current = next;
      setSelectedIds(next);
      if (!additive && isKonvaSafeId(id)) setObjects((prev) => moveObjectToEnd(prev, id));
    }
  };
  const handleShapeClick = (id: string) => {
    cancelLongPress();
    if (longPressFiredRef.current === id) {
      // The long-press already toggled this shape; the release is not a click.
      longPressFiredRef.current = null;
      pendingSelectRef.current = null;
      return;
    }
    const next = selectionOnClick(selectedIdsRef.current, pendingSelectRef.current, id);
    pendingSelectRef.current = null;
    if (next !== selectedIdsRef.current) {
      selectedIdsRef.current = next;
      setSelectedIds(next);
    }
  };

  const handleSelect = useCallback(
    (id: string, evt?: { shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean } | null) => {
      const additive = multiMode || isModifierHeld(evt, heldRef.current);
      setSelectedIds((cur) => nextSelection(cur, id, additive));
      if (!isKonvaSafeId(id)) return; // Should not happen after normalize.
      if (!additive) setObjects((prev) => moveObjectToEnd(prev, id));
    },
    [multiMode]
  );

  const selected = objects.find((obj) => obj.id === selectedObjectId) || null;
  const suggestedLocation =
    selected && !selected.locationId
      ? resolveFacilityLocation(selected, locations, siteId)
      : selected?.locationId
        ? locations.find((l) => Number(l.locationId) === Number(selected.locationId)) || null
        : null;
  const needsLocationLink =
    !!selected &&
    selected.type === 'Storage' &&
    !selected.locationId &&
    !!(selected.name || selected.abbreviation);

  const breweryLocations = locations.filter((l) => !isTaproomLocation(l));
  const taproomLocations = locations.filter((l) => isTaproomLocation(l));
  const selectedLocation =
    selected?.locationId != null
      ? locations.find((l) => Number(l.locationId) === Number(selected.locationId)) || null
      : null;
  const selectedIsTaproom = isTaproomLocation(selectedLocation);
  const locationOptions = (
    <>
      <option value="">Select Location</option>
      {breweryLocations.map((loc) => (
        <option key={loc.locationId} value={loc.locationId}>
          {loc.name}
          {loc.abbreviation ? ` (${loc.abbreviation})` : ''}
        </option>
      ))}
      {taproomLocations.length > 0 && (
        <optgroup label="Taproom (sold, tax paid; not brewery On Hand)">
          {taproomLocations.map((loc) => (
            <option key={loc.locationId} value={loc.locationId} data-taproom="1">
              {loc.name}
              {loc.abbreviation ? ` (${loc.abbreviation})` : ''} · taproom kegs
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
  const taproomNote = selectedIsTaproom ? (
    <p className="designer-taproom-note" data-testid="designer-taproom-note" role="note">
      Taproom: Facility View shows the kegs at {selectedLocation?.name} (sold, tax paid). Not brewery On Hand.
    </p>
  ) : null;
  const showPhoneSheet = isPhone && !!selected;
  // Sit exactly on top of the phone bottom nav: its labels can wrap to two
  // lines, so measure it rather than trusting the 56px --mobile-nav-bar guess.
  const [navHeight, setNavHeight] = useState<number | null>(null);
  useEffect(() => {
    if (!showPhoneSheet) return;
    const measure = () => {
      const nav = document.querySelector('.navbar-mobile') as HTMLElement | null;
      if (!nav) return setNavHeight(null);
      const r = nav.getBoundingClientRect();
      const visible = r.height > 0 && getComputedStyle(nav).display !== 'none';
      setNavHeight(visible ? Math.max(0, Math.round(window.innerHeight - r.top)) : null);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showPhoneSheet]);

  const onStageTouchStart = (e: Konva.KonvaEventObject<TouchEvent>) => {
    lastTouchAtRef.current = Date.now();
    const onEmpty = e.target === e.target.getStage?.();
    const p = onEmpty ? touchPoint(e.evt) : null;
    emptyTapRef.current = p ? tapStart(p.x, p.y, Date.now(), scrollOffset(canvasBoxRef.current)) : null;
  };
  const onStageTouchMove = (e: Konva.KonvaEventObject<TouchEvent>) => {
    lastTouchAtRef.current = Date.now();
    const p = touchPoint(e.evt);
    if (p) emptyTapRef.current = tapMove(emptyTapRef.current, p.x, p.y);
  };
  const onStageTouchEnd = (e: Konva.KonvaEventObject<TouchEvent>) => {
    lastTouchAtRef.current = Date.now();
    const g = emptyTapRef.current;
    emptyTapRef.current = null;
    const p = touchPoint(e.evt);
    if (!g || !p || multiMode) return;
    if (isDeliberateTap(g, p.x, p.y, Date.now(), scrollOffset(canvasBoxRef.current))) setSelectedIds([]);
  };

  const setShapeRef = (id: string, node: Konva.Node | null) => {
    if (node) shapeRefs.current[id] = node;
    else delete shapeRefs.current[id];
  };

  return (
    <div
      className={`page-container${showPhoneSheet ? ' designer--sheet-open' : ''}`}
      style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}
      data-testid="facility-designer"
      data-designer-root
    >
      <div className="page-container container" data-testid="facility-designer-page">
        <h2 className="app-header mb-4" style={{ color: '#333' }}>
          Facility Designer
          {dirty && (
            <span className="designer-dirty-badge" data-testid="unsaved-indicator" role="status">
              ● Unsaved changes
            </span>
          )}
        </h2>
        {draftOffer && (
          <div className="designer-draft-banner" data-testid="draft-banner" role="status">
            Unsaved draft from {new Date(draftOffer.savedAt).toLocaleString('en-US', { timeZone: 'America/Chicago' })} CT
            ({draftOffer.objects.length} shapes).
            <button
              type="button"
              data-testid="restore-draft"
              onClick={() => {
                setObjects(normalizeDesignObjects(draftOffer.objects));
                setDraftOffer(null);
              }}
            >
              Restore unsaved draft
            </button>
            <button
              type="button"
              data-testid="dismiss-draft"
              className="secondary"
              onClick={() => {
                if (siteId) clearDraft(siteId);
                setDraftOffer(null);
              }}
            >
              Dismiss
            </button>
          </div>
        )}
        {guardPrompt && (
          <div className="designer-guard-backdrop" data-testid="unsaved-dialog" role="dialog" aria-modal="true" aria-labelledby="unsaved-title">
            <div className="designer-guard">
              <h3 id="unsaved-title">Unsaved changes</h3>
              <p>
                You have unsaved changes to this facility design. Save them before going to {guardPrompt.label}?
              </p>
              <div className="designer-guard__actions">
                <button type="button" data-testid="unsaved-save" className="is-primary" onClick={() => resolveGuard('save')}>
                  Save
                </button>
                <button type="button" data-testid="unsaved-discard" className="is-danger" onClick={() => resolveGuard('discard')}>
                  Discard
                </button>
                <button type="button" data-testid="unsaved-cancel" onClick={() => resolveGuard('cancel')}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div style={{ color: 'red', marginBottom: '15px' }} role="alert">
            {error}
          </div>
        )}
        {successMessage && (
          <div style={{ color: 'green', marginBottom: '15px' }} data-testid="save-success">
            {successMessage}
          </div>
        )}
        <div className="designer-layout" style={{ display: 'flex' }}>
          <div
            className="designer-panel"
            style={{
              width: '250px',
              marginRight: '20px',
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ marginBottom: '15px' }}>
              <label
                style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}
              >
                Site:
              </label>
              <select
                value={siteId}
                onChange={(e) => {
                  const next = e.target.value;
                  guardThen('another site', () => setSiteId(next));
                }}
                data-testid="site-select"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                }}
              >
                <option value="">Select Site</option>
                {sites.map((site) => (
                  <option key={site.siteId} value={site.siteId}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
            {(
              <div data-testid="assign-panel" style={{ marginBottom: 15 }}>
                <h3 style={{ color: '#555', margin: '0 0 6px', fontSize: '1rem' }}>Assign Location</h3>
                {selected && needsLocationLink && (
                  <div
                    data-testid="location-link-hint"
                    style={{
                      background: '#fff7e6',
                      border: '1px solid #EEC930',
                      borderRadius: 4,
                      padding: 10,
                      marginBottom: 10,
                      fontSize: 13,
                      color: '#333',
                    }}
                  >
                    This storage shape is labeled
                    {selected.abbreviation ? ` “${selected.abbreviation}”` : ''}
                    {selected.name ? ` (${selected.name})` : ''} but has no linked location.
                    {suggestedLocation ? (
                      <>
                        {' '}
                        Suggested: <strong>{suggestedLocation.name}</strong>
                        <button
                          type="button"
                          data-testid="link-suggested-location"
                          onClick={() => assignLocation(Number(suggestedLocation.locationId))}
                          style={{
                            display: 'block',
                            width: '100%',
                            marginTop: 8,
                            padding: '8px 10px',
                            background: '#2196F3',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                          }}
                        >
                          Link to {suggestedLocation.name}
                        </button>
                      </>
                    ) : (
                      <> Pick a location below, then save the design.</>
                    )}
                  </div>
                )}
                <select
                  value={selected?.locationId || ''}
                  disabled={!selected}
                  onChange={(e) => assignLocation(Number(e.target.value))}
                  data-testid="assign-location"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    marginBottom: '10px',
                  }}
                >
                  {locationOptions}
                </select>
                {taproomNote}
                <h3 style={{ color: '#555', margin: '0 0 6px', fontSize: '1rem' }}>Assign Equipment</h3>
                <select
                  value={selected?.equipmentId || ''}
                  disabled={!selected}
                  onChange={(e) => assignEquipment(Number(e.target.value))}
                  data-testid="assign-equipment"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                  }}
                >
                  <option value="">Select Equipment</option>
                  {equipment.map((eq) => (
                    <option key={eq.equipmentId} value={eq.equipmentId}>
                      {eq.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!selected && (
              <p data-testid="assign-hint" style={{ color: '#666', fontSize: 13, margin: '0 0 12px' }}>
                {selectedIds.length > 1
                  ? 'Select a single shape to assign its location or equipment.'
                  : 'Select a shape to assign its location or equipment.'}
              </p>
            )}
            <div style={{ marginBottom: '15px' }}>
              <button
                type="button"
                onClick={() => addObject('Tank')}
                data-testid="add-tank"
                style={{
                  width: '100%',
                  padding: '10px',
                  marginBottom: '10px',
                  backgroundColor: '#2196F3',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                Add Tank
              </button>
              <button
                type="button"
                onClick={() => addObject('Barrel')}
                data-testid="add-barrel"
                style={{
                  width: '100%',
                  padding: '10px',
                  marginBottom: '10px',
                  backgroundColor: '#8D5524',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                Add Barrel
              </button>
              <button
                type="button"
                onClick={() => addObject('Storage')}
                data-testid="add-storage"
                style={{
                  width: '100%',
                  padding: '10px',
                  marginBottom: '10px',
                  backgroundColor: '#2196F3',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                Add Storage
              </button>
              <button
                type="button"
                onClick={deleteObject}
                disabled={!selectedIds.length}
                data-testid="delete-selected"
                style={{
                  width: '100%',
                  padding: '10px',
                  marginBottom: '10px',
                  backgroundColor: selectedIds.length ? '#F86752' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedIds.length ? 'pointer' : 'not-allowed',
                  fontSize: '16px',
                }}
              >
                {selectedIds.length > 1 ? `Delete Selected (${selectedIds.length})` : 'Delete Selected'}
              </button>
              <button
                type="button"
                onClick={() => setMultiMode((m) => !m)}
                aria-pressed={multiMode}
                data-testid="select-multiple-toggle"
                style={{
                  width: '100%',
                  padding: '10px',
                  marginBottom: '10px',
                  backgroundColor: multiMode ? '#1565C0' : '#fff',
                  color: multiMode ? '#fff' : '#1565C0',
                  border: '2px solid #1565C0',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                {multiMode ? 'Select multiple: ON' : 'Select multiple'}
              </button>
              <button
                type="button"
                onClick={undo}
                disabled={!historyLen}
                data-testid="undo"
                style={{
                  width: '100%',
                  padding: '8px',
                  marginBottom: '10px',
                  background: '#fff',
                  color: historyLen ? '#333' : '#aaa',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: historyLen ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                }}
              >
                Undo
              </button>
            </div>

            <div style={{ marginBottom: '15px' }} data-testid="object-list">
              <h3 style={{ color: '#555', marginBottom: '8px', fontSize: '1rem' }}>Objects</h3>
              {objects.length === 0 ? (
                <p style={{ color: '#777', fontSize: '14px' }}>No objects yet.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {objects.map((obj) => (
                    <li key={obj.id} style={{ marginBottom: '6px' }}>
                      <button
                        type="button"
                        data-testid={`select-object-${obj.id}`}
                        data-object-id={obj.id}
                        onClick={(e) => handleSelect(obj.id, e)}
                        aria-pressed={selectedIds.includes(obj.id)}
                        className={`designer-object-row${selectedIds.includes(obj.id) ? ' is-selected' : ''}`}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px',
                          border:
                            selectedIds.includes(obj.id) ? '2px solid #1976D2' : '1px solid #ddd',
                          borderRadius: '4px',
                          background: selectedIds.includes(obj.id) ? '#E3F2FD' : '#fff',
                          color: '#222',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                      >
                        {obj.type}
                        {obj.abbreviation ? ` · ${obj.abbreviation}` : ''}
                        {obj.name ? ` (${obj.name})` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              type="button"
              onClick={() => saveDesign()}
              data-testid="save-design"
              className={`designer-save${dirty ? ' is-dirty' : ''}`}
              style={{
                width: '100%',
                padding: '10px',
                marginTop: '20px',
                backgroundColor: dirty ? '#E65100' : '#2196F3',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: dirty ? 700 : 400,
              }}
            >
              {dirty ? 'Save Design • unsaved changes' : 'Save Design'}
            </button>
          </div>
          <div
            style={{
              flex: 1,
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
            data-testid="design-canvas"
            ref={canvasBoxRef}
          >
            <div className="designer-align-bar" data-testid="align-toolbar" role="toolbar" aria-label="Align selected shapes">
              <button
                type="button"
                className={`designer-multi-toggle${multiMode ? ' is-on' : ''}`}
                aria-pressed={multiMode}
                data-testid="select-multiple-toolbar"
                onClick={() => setMultiMode((m) => !m)}
              >
                {multiMode ? '✓ Select multiple: ON (tap to turn off)' : '☐ Select multiple (tap shapes to add)'}
              </button>
              <span className="designer-align-bar__count" data-testid="selection-count">
                {longPressFlash
                  ? `${selectedIds.includes(longPressFlash) ? 'Added' : 'Removed'} ${longPressFlash.toUpperCase()} · ${selectedIds.length} selected`
                  : selectedIds.length > 1
                    ? `${selectedIds.length} selected`
                    : multiMode
                      ? 'Select multiple is ON: tap shapes to add or remove them'
                      : 'Shift/Ctrl/⌘-click, drag a box, long-press a shape, or turn on Select multiple'}
              </span>
              {(
                [
                  ['left', 'Align left', '⇤'],
                  ['center', 'Align center', '↔'],
                  ['right', 'Align right', '⇥'],
                  ['top', 'Align top', '⤒'],
                  ['middle', 'Align middle', '↕'],
                  ['bottom', 'Align bottom', '⤓'],
                ] as [AlignMode, string, string][]
              ).map(([mode, label, icon]) => (
                <button
                  key={mode}
                  type="button"
                  title={label}
                  aria-label={label}
                  data-testid={`align-${mode}`}
                  disabled={selectedIds.length < 2}
                  onClick={() => applyAlign(mode)}
                >
                  <span aria-hidden="true">{icon}</span> {label.replace('Align ', '')}
                </button>
              ))}
              <button
                type="button"
                title="Distribute horizontally (equal gaps)"
                aria-label="Distribute horizontally"
                data-testid="distribute-horizontal"
                disabled={selectedIds.length < 3}
                onClick={() => applyDistribute('horizontal')}
              >
                ⇹ Space across
              </button>
              <button
                type="button"
                title="Distribute vertically (equal gaps)"
                aria-label="Distribute vertically"
                data-testid="distribute-vertical"
                disabled={selectedIds.length < 3}
                onClick={() => applyDistribute('vertical')}
              >
                ⇳ Space down
              </button>
            </div>
            <Stage
              width={800}
              height={600}
              ref={stageRef}
              onMouseDown={(e) => {
                // Compatibility mouse events right after a touch: the touch handlers own that gesture.
                if (Date.now() - lastTouchAtRef.current < 800) return;
                // Empty stage: start a drag-select box (shift keeps the current selection).
                if (e.target === e.target.getStage()) {
                  const pos = e.target.getStage()?.getPointerPosition?.();
                  if (!e.evt?.shiftKey && !multiMode) setSelectedIds([]);
                  if (pos) setSelectBox({ left: pos.x, top: pos.y, right: pos.x, bottom: pos.y });
                }
              }}
              onMouseMove={(e) => {
                if (!selectBox) return;
                const pos = e.target.getStage()?.getPointerPosition?.();
                if (pos) setSelectBox((b) => (b ? { ...b, right: pos.x, bottom: pos.y } : b));
              }}
              onMouseUp={() => {
                if (!selectBox) return;
                const big =
                  Math.abs(selectBox.right - selectBox.left) > 4 || Math.abs(selectBox.bottom - selectBox.top) > 4;
                if (big) {
                  const hits = idsInBox(objects, selectBox);
                  setSelectedIds((cur) => Array.from(new Set([...cur, ...hits])));
                }
                setSelectBox(null);
              }}
              // Touch on empty canvas is usually the start of a page scroll: never
              // deselect on touchstart/move. Only a short, still tap deselects.
              onTouchStart={onStageTouchStart}
              onTouchMove={onStageTouchMove}
              onTouchEnd={onStageTouchEnd}
              onTouchCancel={() => {
                emptyTapRef.current = null;
              }}
            >
              <Layer>
                {objects.map((obj) =>
                  obj.shape === 'circle' ? (
                    <React.Fragment key={obj.id}>
                      <Circle
                        id={obj.id}
                        name={obj.id}
                        ref={(node) => setShapeRef(obj.id, node)}
                        x={obj.x}
                        y={obj.y}
                        radius={obj.radius}
                        fill={obj.type === 'Barrel' ? '#D7B48A' : '#90CAF9'}
                        stroke={selectedIds.includes(obj.id) ? '#1565C0' : 'black'}
                        strokeWidth={selectedIds.includes(obj.id) ? 3 : 1}
                        dash={selectedIds.length > 1 && selectedIds.includes(obj.id) ? [6, 3] : undefined}
                        shadowColor={selectedIds.length > 1 && selectedIds.includes(obj.id) ? '#1565C0' : undefined}
                        shadowBlur={selectedIds.length > 1 && selectedIds.includes(obj.id) ? 8 : 0}
                        draggable
                        onDragStart={() => handleDragStart(obj.id)}
                        onDragMove={(e: Konva.KonvaEventObject<DragEvent>) =>
                          handleDragMove(obj.id, e.target.x(), e.target.y())
                        }
                        onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) =>
                          handleDragEnd(obj.id, e.target.x(), e.target.y())
                        }
                        onPointerDown={(e: Konva.KonvaEventObject<PointerEvent>) =>
                          handleShapePointerDown(obj.id, e?.evt)
                        }
                        onPointerClick={() => handleShapeClick(obj.id)}
                        onPointerUp={() => cancelLongPress()}
                        onPointerCancel={() => cancelLongPress()}
                        onTransformEnd={(e: Konva.KonvaEventObject<Event>) =>
                          handleTransformEnd(obj.id, e.target)
                        }
                      />
                      <Text
                        name={`label-${obj.id}`}
                        x={obj.x - (obj.radius || 30)}
                        y={obj.y - 6}
                        width={(obj.radius || 30) * 2}
                        height={12}
                        text={obj.abbreviation}
                        fontSize={Math.max(8, Math.min(12, (obj.radius || 30) / 2.2))}
                        fontFamily="Arial"
                        fill="black"
                        align="center"
                        verticalAlign="middle"
                        listening={false}
                      />
                    </React.Fragment>
                  ) : (
                    <React.Fragment key={obj.id}>
                      <Rect
                        id={obj.id}
                        name={obj.id}
                        ref={(node) => setShapeRef(obj.id, node)}
                        x={obj.x}
                        y={obj.y}
                        width={obj.width}
                        height={obj.height}
                        fill="#A5D6A7"
                        stroke={selectedIds.includes(obj.id) ? '#2E7D32' : 'black'}
                        strokeWidth={selectedIds.includes(obj.id) ? 3 : 1}
                        dash={selectedIds.length > 1 && selectedIds.includes(obj.id) ? [6, 3] : undefined}
                        shadowColor={selectedIds.length > 1 && selectedIds.includes(obj.id) ? '#1565C0' : undefined}
                        shadowBlur={selectedIds.length > 1 && selectedIds.includes(obj.id) ? 8 : 0}
                        draggable
                        onDragStart={() => handleDragStart(obj.id)}
                        onDragMove={(e: Konva.KonvaEventObject<DragEvent>) =>
                          handleDragMove(obj.id, e.target.x(), e.target.y())
                        }
                        onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) =>
                          handleDragEnd(obj.id, e.target.x(), e.target.y())
                        }
                        onPointerDown={(e: Konva.KonvaEventObject<PointerEvent>) =>
                          handleShapePointerDown(obj.id, e?.evt)
                        }
                        onPointerClick={() => handleShapeClick(obj.id)}
                        onPointerUp={() => cancelLongPress()}
                        onPointerCancel={() => cancelLongPress()}
                        onTransformEnd={(e: Konva.KonvaEventObject<Event>) =>
                          handleTransformEnd(obj.id, e.target)
                        }
                      />
                      <Text
                        name={`label-${obj.id}`}
                        x={obj.x}
                        y={obj.y + ((obj.height || 60) / 2) - 6}
                        width={obj.width || 100}
                        height={12}
                        text={obj.abbreviation}
                        fontSize={12}
                        fontFamily="Arial"
                        fill="black"
                        align="center"
                        verticalAlign="middle"
                        listening={false}
                      />
                    </React.Fragment>
                  )
                )}
                {selectBox && (
                  <Rect
                    x={Math.min(selectBox.left, selectBox.right)}
                    y={Math.min(selectBox.top, selectBox.bottom)}
                    width={Math.abs(selectBox.right - selectBox.left)}
                    height={Math.abs(selectBox.bottom - selectBox.top)}
                    fill="rgba(21,101,192,0.08)"
                    stroke="#1565C0"
                    dash={[4, 4]}
                    listening={false}
                  />
                )}
                <Transformer
                  ref={transformerRef}
                  rotateEnabled={false}
                  enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
                  keepRatio={objects.find((o) => o.id === selectedObjectId)?.shape === 'circle'}
                  anchorSize={12}
                  boundBoxFunc={(oldBox: BoundingBox, newBox: BoundingBox) => {
                    if (Math.abs(newBox.width) < MIN_SHAPE_SIZE || Math.abs(newBox.height) < MIN_SHAPE_SIZE) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              </Layer>
            </Stage>
          </div>
        </div>
      </div>
      {showPhoneSheet &&
        selected &&
        createPortal(
          <div
            className="designer-phone-sheet"
            data-testid="designer-phone-sheet"
            data-designer-root
            style={navHeight != null ? { bottom: navHeight } : undefined}
            role="dialog"
            aria-label="Selected shape properties"
          >
            <div className="designer-phone-sheet__head">
              <strong data-testid="designer-phone-sheet-title">
                {selected.type}
                {selected.abbreviation ? ` · ${selected.abbreviation}` : ''}
                {selected.name ? ` (${selected.name})` : ''}
              </strong>
              <button
                type="button"
                className="designer-phone-sheet__done"
                data-testid="designer-phone-sheet-done"
                onClick={() => setSelectedIds([])}
              >
                Done
              </button>
            </div>
            {needsLocationLink && suggestedLocation && (
              <button
                type="button"
                className="designer-phone-sheet__suggest"
                data-testid="phone-link-suggested-location"
                onClick={() => assignLocation(Number(suggestedLocation.locationId))}
              >
                Link to {suggestedLocation.name}
              </button>
            )}
            <label className="designer-phone-sheet__label" htmlFor="phone-assign-location">
              Location
            </label>
            <select
              id="phone-assign-location"
              value={selected.locationId || ''}
              onChange={(e) => assignLocation(Number(e.target.value))}
              data-testid="phone-assign-location"
            >
              {locationOptions}
            </select>
            {taproomNote}
            <label className="designer-phone-sheet__label" htmlFor="phone-assign-equipment">
              Equipment
            </label>
            <select
              id="phone-assign-equipment"
              value={selected.equipmentId || ''}
              onChange={(e) => assignEquipment(Number(e.target.value))}
              data-testid="phone-assign-equipment"
            >
              <option value="">Select Equipment</option>
              {equipment.map((eq) => (
                <option key={eq.equipmentId} value={eq.equipmentId}>
                  {eq.name}
                </option>
              ))}
            </select>
            <div className="designer-phone-sheet__actions">
              <button
                type="button"
                className="is-danger"
                data-testid="phone-delete-selected"
                onClick={deleteObject}
              >
                Delete
              </button>
              <button
                type="button"
                className={`is-primary${dirty ? ' is-dirty' : ''}`}
                data-testid="phone-save-design"
                onClick={() => saveDesign()}
              >
                {dirty ? 'Save Design •' : 'Save Design'}
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default FacilityDesigner;
